import * as THREE from 'three';
import { EXPERIENCES, Experience } from '../data/experiences';

export class ComputerScreen {
  public canvas: HTMLCanvasElement;
  public ctx: CanvasRenderingContext2D;
  public texture: THREE.CanvasTexture;
  public material: THREE.MeshBasicMaterial;

  private activeExperience: Experience = EXPERIENCES.racer;
  private time: number = 0;
  private terminalLogs: string[] = [
    'TOILLE-OS KERNEL v4.9.2 (x86_64-toille-cyber)',
    'SYS_INITIALIZE: WebGL 3D Matrix pipeline ready',
    'NET: Connected to edge gateway [toille.uk]',
    'LOADED: toille-racer & toille-ide modules',
    'READY FOR SELECTION'
  ];

  // Animated elements
  private carX: number = 0;
  private carTargetX: number = 0;
  private roadOffset: number = 0;
  private codeScroll: number = 0;
  private cursorBlink: number = 0;
  private glitchTimer: number = 0;
  private glitchActive: boolean = false;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1024;
    this.canvas.height = 768;
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Could not get 2D context for screen');
    this.ctx = context;

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.generateMipmaps = true;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.magFilter = THREE.LinearFilter;

    // Sharp, crystal-clear CRT monitor material without blinding glare
    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
    });
  }

  public setExperience(exp: Experience) {
    this.activeExperience = exp;
    this.addLog(`CARTRIDGE MOUNTED: [${exp.title}]`);
    this.addLog(`TARGET ENDPOINT: ${exp.primaryUrl}`);
    this.texture.needsUpdate = true;
  }

  public addLog(message: string) {
    const timeStr = new Date().toISOString().substring(11, 19);
    this.terminalLogs.push(`[${timeStr}] ${message}`);
    if (this.terminalLogs.length > 7) {
      this.terminalLogs.shift();
    }
    this.texture.needsUpdate = true;
  }

  public update(deltaTime: number) {
    this.time += deltaTime;
    this.roadOffset = (this.roadOffset + deltaTime * 240) % 80;
    this.codeScroll += deltaTime * 25;
    this.cursorBlink = (this.cursorBlink + deltaTime * 3) % 2;

    // Car gentle sway
    this.carTargetX = Math.sin(this.time * 1.5) * 60;
    this.carX += (this.carTargetX - this.carX) * 0.08;

    // Glitch trigger
    this.glitchTimer += deltaTime;
    if (this.glitchTimer > 6.0) {
      this.glitchActive = true;
      if (this.glitchTimer > 6.18) {
        this.glitchActive = false;
        this.glitchTimer = 0;
      }
    }

    this.renderCanvas();
    this.texture.needsUpdate = true;
  }

  private renderCanvas() {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;
    const exp = this.activeExperience;

    // Clear dark cyber slate
    ctx.fillStyle = '#060810';
    ctx.fillRect(0, 0, w, h);

    // Subtle CRT background grid
    ctx.strokeStyle = 'rgba(0, 255, 242, 0.04)';
    ctx.lineWidth = 1;
    const gridSize = 32;
    for (let x = 0; x < w; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y < h; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // Top Header Status Bar
    this.renderHeader(w);

    // Main central preview viewport
    if (exp.id === 'racer') {
      this.renderRacerPreview(w, h);
    } else if (exp.id === 'ide') {
      this.renderIdePreview(w, h);
    } else {
      this.renderFpsPreview(w, h);
    }

    // Bottom Terminal & Action Bar
    this.renderBottomPanel(w, h);

    // CRT Scanline Overlay
    this.renderScanlines(w, h);

    // Glitch effect if active
    if (this.glitchActive) {
      const sliceY = (Math.random() * h) | 0;
      const sliceH = (20 + Math.random() * 40) | 0;
      const shiftX = (Math.random() * 40 - 20) | 0;
      try {
        ctx.drawImage(canvas, 0, sliceY, w, sliceH, shiftX, sliceY, w, sliceH);
      } catch {
        // safe ignore
      }
    }

    // CRT Border Glow / Vignette
    const gradient = ctx.createRadialGradient(w / 2, h / 2, h * 0.45, w / 2, h / 2, w * 0.65);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.65)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
  }

  private renderHeader(w: number) {
    const { ctx } = this;

    // Header container
    ctx.fillStyle = 'rgba(10, 16, 28, 0.95)';
    ctx.fillRect(0, 0, w, 56);
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, w, 56);

    // OS Title
    ctx.font = 'bold 20px "Share Tech Mono", monospace';
    ctx.fillStyle = '#00fff2';
    ctx.fillText('TOILLE-OS // CYBER-ARCADE TERMINAL', 24, 36);

    // Clock
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-GB') + '.' + Math.floor(now.getMilliseconds() / 100);
    ctx.font = '16px "Share Tech Mono", monospace';
    ctx.fillStyle = '#ff007f';
    ctx.fillText(`UTC ${timeStr}`, w - 210, 36);

    // Status pill
    ctx.fillStyle = '#39ff14';
    ctx.beginPath();
    ctx.arc(w - 235, 30, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  private renderRacerPreview(w: number, _h: number) {
    const { ctx } = this;
    const viewY = 70;
    const viewW = w - 48;
    const viewH = 430;
    const viewX = 24;

    // Viewport frame
    ctx.save();
    ctx.beginPath();
    ctx.rect(viewX, viewY, viewW, viewH);
    ctx.clip();

    // Deep synthwave sunset sky
    const skyGrad = ctx.createLinearGradient(0, viewY, 0, viewY + viewH);
    skyGrad.addColorStop(0, '#0d0221');
    skyGrad.addColorStop(0.45, '#3b0066');
    skyGrad.addColorStop(0.7, '#ff007f');
    skyGrad.addColorStop(0.85, '#ff7700');
    skyGrad.addColorStop(1, '#05030d');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(viewX, viewY, viewW, viewH);

    // Synthwave Sun on horizon
    const sunX = w / 2;
    const horizonY = viewY + 230;
    const sunRadius = 75;

    ctx.save();
    ctx.beginPath();
    ctx.arc(sunX, horizonY - 15, sunRadius, 0, Math.PI * 2);
    const sunGrad = ctx.createLinearGradient(0, horizonY - 15 - sunRadius, 0, horizonY - 15 + sunRadius);
    sunGrad.addColorStop(0, '#ffff00');
    sunGrad.addColorStop(0.6, '#ff007f');
    sunGrad.addColorStop(1, '#7700ff');
    ctx.fillStyle = sunGrad;
    ctx.fill();

    // Sun horizontal slice bands
    ctx.fillStyle = '#0d0221';
    for (let i = 0; i < 9; i++) {
      const sliceY = horizonY - 30 + i * 11;
      const sliceHeight = 2 + i * 1.2;
      ctx.fillRect(sunX - sunRadius - 10, sliceY, (sunRadius + 10) * 2, sliceHeight);
    }
    ctx.restore();

    // Perspective Neon Grid (Ground)
    ctx.fillStyle = '#060212';
    ctx.fillRect(viewX, horizonY, viewW, viewY + viewH - horizonY);

    // Horizontal road perspective lines
    ctx.strokeStyle = 'rgba(255, 0, 127, 0.8)';
    ctx.lineWidth = 1.5;
    const numHorizontal = 16;
    for (let i = 1; i <= numHorizontal; i++) {
      const factor = Math.pow(i / numHorizontal, 2.2);
      const y = horizonY + factor * (viewY + viewH - horizonY);
      ctx.beginPath();
      ctx.moveTo(viewX, y);
      ctx.lineTo(viewX + viewW, y);
      ctx.stroke();
    }

    // Perspective radial lines converging to sun center
    const numRadials = 24;
    for (let i = 0; i <= numRadials; i++) {
      const bottomX = viewX + (i / numRadials) * viewW;
      ctx.beginPath();
      ctx.moveTo(sunX, horizonY);
      ctx.lineTo(bottomX, viewY + viewH);
      ctx.stroke();
    }

    // Speeding Road Ribbon
    const roadTopW = 70;
    const roadBottomW = 540;
    ctx.fillStyle = 'rgba(12, 10, 24, 0.9)';
    ctx.beginPath();
    ctx.moveTo(sunX - roadTopW / 2, horizonY);
    ctx.lineTo(sunX + roadTopW / 2, horizonY);
    ctx.lineTo(sunX + roadBottomW / 2, viewY + viewH);
    ctx.lineTo(sunX - roadBottomW / 2, viewY + viewH);
    ctx.closePath();
    ctx.fill();

    // Road glowing borders
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 4;
    ctx.shadowColor = '#00fff2';
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.moveTo(sunX - roadTopW / 2, horizonY);
    ctx.lineTo(sunX - roadBottomW / 2, viewY + viewH);
    ctx.moveTo(sunX + roadTopW / 2, horizonY);
    ctx.lineTo(sunX + roadBottomW / 2, viewY + viewH);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Road center dashes
    ctx.strokeStyle = '#ffff00';
    ctx.lineWidth = 3;
    const dashes = 9;
    for (let i = 0; i < dashes; i++) {
      const prog = (i / dashes + (this.roadOffset / 80)) % 1;
      const y = horizonY + Math.pow(prog, 2) * (viewY + viewH - horizonY);
      const nextY = y + 16 * prog;
      ctx.beginPath();
      ctx.moveTo(sunX, y);
      ctx.lineTo(sunX, nextY);
      ctx.stroke();
    }

    // Cyberpunk Supercar Wireframe / Silhouette
    const carBaseX = sunX + this.carX;
    const carBaseY = viewY + viewH - 55;

    // Under-car neon glow
    const underGlow = ctx.createRadialGradient(carBaseX, carBaseY + 10, 5, carBaseX, carBaseY + 10, 90);
    underGlow.addColorStop(0, 'rgba(0, 255, 242, 0.9)');
    underGlow.addColorStop(1, 'rgba(0, 255, 242, 0)');
    ctx.fillStyle = underGlow;
    ctx.fillRect(carBaseX - 110, carBaseY - 10, 220, 45);

    // Car Body
    ctx.fillStyle = '#0a0a14';
    ctx.strokeStyle = '#ff007f';
    ctx.lineWidth = 2.5;

    ctx.beginPath();
    ctx.moveTo(carBaseX - 70, carBaseY); // left rear tire
    ctx.lineTo(carBaseX - 75, carBaseY - 20); // rear quarter
    ctx.lineTo(carBaseX - 55, carBaseY - 32); // rear wing
    ctx.lineTo(carBaseX - 35, carBaseY - 45); // roof left
    ctx.lineTo(carBaseX + 35, carBaseY - 45); // roof right
    ctx.lineTo(carBaseX + 55, carBaseY - 32); // rear wing right
    ctx.lineTo(carBaseX + 75, carBaseY - 20);
    ctx.lineTo(carBaseX + 70, carBaseY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Neon Taillight Bar
    ctx.strokeStyle = '#ff0033';
    ctx.lineWidth = 6;
    ctx.shadowColor = '#ff0033';
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.moveTo(carBaseX - 60, carBaseY - 16);
    ctx.lineTo(carBaseX + 60, carBaseY - 16);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // License plate / badge
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = '#00fff2';
    ctx.fillText('TOILLE-2088', carBaseX - 30, carBaseY - 4);

    // Cockpit HUD HUD Overlay inside the frame
    ctx.font = 'bold 28px "Orbitron", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#00fff2';
    ctx.shadowBlur = 12;
    ctx.fillText('TOILLE-RACER', viewX + 30, viewY + 50);
    ctx.shadowBlur = 0;

    ctx.font = '14px "Share Tech Mono", monospace';
    ctx.fillStyle = '#00fff2';
    ctx.fillText('ARCADE SIMULATOR · SOLIDJS + WEB AUDIO + WEBRTC', viewX + 30, viewY + 75);

    // Speedometer Box
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.strokeStyle = '#ff007f';
    ctx.lineWidth = 1.5;
    ctx.fillRect(viewX + viewW - 200, viewY + 25, 175, 80);
    ctx.strokeRect(viewX + viewW - 200, viewY + 25, 175, 80);

    const speed = Math.floor(290 + Math.sin(this.time * 2) * 35);
    ctx.font = 'bold 34px "Orbitron", sans-serif';
    ctx.fillStyle = '#ffff00';
    ctx.fillText(`${speed}`, viewX + viewW - 185, viewY + 68);
    ctx.font = '12px "Share Tech Mono", monospace';
    ctx.fillStyle = '#ff007f';
    ctx.fillText('KM/H  [GEAR 6]', viewX + viewW - 100, viewY + 68);
    ctx.fillStyle = '#00fff2';
    ctx.fillText('COCKPIT TELEMETRY SYNC', viewX + viewW - 185, viewY + 92);

    // Prominent Launch Button
    const btnW = 380;
    const btnH = 46;
    const btnX = sunX - btnW / 2;
    const btnY = viewY + viewH - 75;

    ctx.fillStyle = 'rgba(0, 255, 242, 0.2)';
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 2;
    ctx.fillRect(btnX, btnY, btnW, btnH);
    ctx.strokeRect(btnX, btnY, btnW, btnH);

    ctx.font = 'bold 18px "Orbitron", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText('⚡ LAUNCH TOILLE-RACER (racer.toille.uk)', sunX, btnY + 29);
    ctx.textAlign = 'left';

    ctx.restore();
  }

  private renderIdePreview(w: number, _h: number) {
    const { ctx } = this;
    const viewY = 70;
    const viewW = w - 48;
    const viewH = 430;
    const viewX = 24;

    ctx.save();
    ctx.beginPath();
    ctx.rect(viewX, viewY, viewW, viewH);
    ctx.clip();

    // Dark IDE background
    ctx.fillStyle = '#0a0d14';
    ctx.fillRect(viewX, viewY, viewW, viewH);

    // IDE Tab bar
    ctx.fillStyle = '#101726';
    ctx.fillRect(viewX, viewY, viewW, 36);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1;
    ctx.strokeRect(viewX, viewY, viewW, 36);

    // Tabs
    const tabs = [
      { name: 'main.ts', active: true, color: '#3178c6' },
      { name: 'worker-sandbox.ts', active: false, color: '#eab308' },
      { name: 'php-runtime.wasm', active: false, color: '#777bb4' },
      { name: 'vim-engine.ts', active: false, color: '#22c55e' }
    ];

    let tabX = viewX + 10;
    tabs.forEach((tab) => {
      ctx.fillStyle = tab.active ? '#182234' : 'rgba(15, 23, 42, 0.6)';
      ctx.fillRect(tabX, viewY + 4, 150, 31);
      if (tab.active) {
        ctx.strokeStyle = '#00fff2';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(tabX, viewY + 4);
        ctx.lineTo(tabX + 150, viewY + 4);
        ctx.stroke();
      }
      ctx.font = '13px "Share Tech Mono", monospace';
      ctx.fillStyle = tab.active ? '#ffffff' : '#94a3b8';
      ctx.fillText(tab.name, tabX + 16, viewY + 24);
      tabX += 156;
    });

    // Left line numbers gutter
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(viewX, viewY + 36, 50, viewH - 36);

    // Simulated Code lines
    const codeLines = [
      { num: '01', text: '// TOILLE-IDE: Browser-native WASM & Worker Workspace', type: 'comment' },
      { num: '02', text: 'import { createSandboxWorker } from "@toille/worker-runtime";', type: 'import' },
      { num: '03', text: 'import { initPhpWasmEngine } from "@toille/php-runtime";', type: 'import' },
      { num: '04', text: '', type: 'text' },
      { num: '05', text: 'export async function executeCyberModule(code: string) {', type: 'func' },
      { num: '06', text: '  const worker = await createSandboxWorker({ isolation: "strict" });', type: 'code' },
      { num: '07', text: '  const telemetry = await worker.evaluate(code);', type: 'code' },
      { num: '08', text: '  console.log(`[EXECUTION RESULT] status: OK, speed: 0.8ms`);', type: 'code' },
      { num: '09', text: '  return telemetry.metrics;', type: 'code' },
      { num: '10', text: '}', type: 'func' },
      { num: '11', text: '', type: 'text' },
      { num: '12', text: '// Vim Modal Engine: NORMAL MODE (Press :w to save, :q to exit)', type: 'comment' },
      { num: '13', text: 'const vimState = { mode: "NORMAL", cursor: [8, 42], buffers: 4 };', type: 'code' }
    ];

    let lineY = viewY + 68;
    codeLines.forEach((line) => {
      // line number
      ctx.font = '13px "Share Tech Mono", monospace';
      ctx.fillStyle = '#475569';
      ctx.fillText(line.num, viewX + 14, lineY);

      // code text
      if (line.type === 'comment') {
        ctx.fillStyle = '#64748b';
      } else if (line.type === 'import') {
        ctx.fillStyle = '#c084fc';
      } else if (line.type === 'func') {
        ctx.fillStyle = '#38bdf8';
      } else {
        ctx.fillStyle = '#e2e8f0';
      }
      ctx.fillText(line.text, viewX + 68, lineY);
      lineY += 24;
    });

    // Blinking Vim Cursor on line 8
    if (this.cursorBlink > 1) {
      ctx.fillStyle = '#39ff14';
      ctx.fillRect(viewX + 68 + 485, viewY + 68 + 24 * 7 - 14, 10, 16);
    }

    // Terminal / Diagnostics Console Drawer (Bottom of IDE preview)
    const termY = viewY + viewH - 120;
    ctx.fillStyle = '#05070d';
    ctx.fillRect(viewX + 50, termY, viewW - 50, 120);
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 1;
    ctx.strokeRect(viewX + 50, termY, viewW - 50, 120);

    // Diagnostics bar
    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.fillStyle = '#39ff14';
    ctx.fillText('● WORKER READY   ● 0 DIAGNOSTIC ERRORS   ● VIM NORMAL   ● PHP 8.3 WASM MOUNTED', viewX + 68, termY + 24);

    ctx.font = '12px "Share Tech Mono", monospace';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('> [CONSOLE STREAM] Initialized secure evaluation thread in 1.4ms', viewX + 68, termY + 50);
    ctx.fillStyle = '#00fff2';
    ctx.fillText('> [SANDBOX] Live playground active at https://ide.toille.uk', viewX + 68, termY + 72);

    // Prominent Launch Button
    const btnW = 380;
    const btnH = 40;
    const btnX = w / 2 - btnW / 2;
    const btnY = termY + 76;

    ctx.fillStyle = 'rgba(0, 255, 242, 0.25)';
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 2;
    ctx.fillRect(btnX, btnY, btnW, btnH);
    ctx.strokeRect(btnX, btnY, btnW, btnH);

    ctx.font = 'bold 16px "Orbitron", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText('⚡ LAUNCH TOILLE-IDE (ide.toille.uk)', w / 2, btnY + 26);
    ctx.textAlign = 'left';

    ctx.restore();
  }

  private renderFpsPreview(w: number, _h: number) {
    const { ctx } = this;
    const viewY = 70;
    const viewW = w - 48;
    const viewH = 430;
    const viewX = 24;

    ctx.save();
    ctx.beginPath();
    ctx.rect(viewX, viewY, viewW, viewH);
    ctx.clip();

    // Deep cosmic night sky
    const skyGrad = ctx.createLinearGradient(0, viewY, 0, viewY + viewH);
    skyGrad.addColorStop(0, '#04060e');
    skyGrad.addColorStop(0.5, '#120b24');
    skyGrad.addColorStop(1, '#060814');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(viewX, viewY, viewW, viewH);

    // Distant Starfield & Nebula
    ctx.fillStyle = 'rgba(255, 170, 0, 0.08)';
    ctx.beginPath();
    ctx.arc(viewX + viewW * 0.7, viewY + 120, 140, 0, Math.PI * 2);
    ctx.fill();

    // Three Skyscraper Towers of DM-Morpheus
    const horizonY = viewY + viewH - 40;

    // Tower 1 (Alpha - Tall Center Tower)
    const t1X = viewX + viewW * 0.45;
    const t1W = 180;
    const t1H = 240;
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(t1X - t1W / 2, horizonY - t1H, t1W, t1H + 40);
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(t1X - t1W / 2, horizonY - t1H, t1W, t1H + 40);

    // Tower 1 Jump Pad Ring (pulsing cyan)
    const padPulse = Math.sin(this.time * 6) * 4;
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(t1X, horizonY - t1H + 20, 35 + padPulse, 12, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Tower 2 (Beta - Left Tower)
    const t2X = viewX + viewW * 0.18;
    const t2W = 140;
    const t2H = 180;
    ctx.fillStyle = '#0c1220';
    ctx.fillRect(t2X - t2W / 2, horizonY - t2H, t2W, t2H + 40);
    ctx.strokeStyle = 'rgba(0, 255, 242, 0.4)';
    ctx.lineWidth = 1;
    ctx.strokeRect(t2X - t2W / 2, horizonY - t2H, t2W, t2H + 40);

    // Tower 3 (Gamma - Right Tower)
    const t3X = viewX + viewW * 0.82;
    const t3W = 150;
    const t3H = 160;
    ctx.fillStyle = '#0c1220';
    ctx.fillRect(t3X - t3W / 2, horizonY - t3H, t3W, t3H + 40);
    ctx.strokeStyle = 'rgba(0, 255, 242, 0.4)';
    ctx.lineWidth = 1;
    ctx.strokeRect(t3X - t3W / 2, horizonY - t3H, t3W, t3H + 40);

    // Tactical UT Reticle in Center
    const retX = viewX + viewW / 2;
    const retY = viewY + viewH / 2 - 15;
    ctx.strokeStyle = '#ffaa00';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(retX, retY, 14, 0, Math.PI * 2);
    ctx.moveTo(retX - 22, retY);
    ctx.lineTo(retX - 16, retY);
    ctx.moveTo(retX + 16, retY);
    ctx.lineTo(retX + 22, retY);
    ctx.moveTo(retX, retY - 22);
    ctx.lineTo(retX, retY - 16);
    ctx.moveTo(retX, retY + 16);
    ctx.lineTo(retX, retY + 22);
    ctx.stroke();

    // Announcer Banner in Viewport
    ctx.font = 'bold 24px "Orbitron", sans-serif';
    ctx.fillStyle = '#ffea00';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#ffaa00';
    ctx.shadowBlur = 12;
    ctx.fillText('HEADSHOT!', retX, retY - 60);
    ctx.shadowBlur = 0;

    // HUD Stats Overlay
    ctx.textAlign = 'left';
    ctx.font = 'bold 18px "Orbitron", sans-serif';
    ctx.fillStyle = '#22c55e';
    ctx.fillText('HEALTH: 100', viewX + 30, viewY + viewH - 75);
    ctx.fillStyle = '#38bdf8';
    ctx.fillText('SHIELD: 50', viewX + 180, viewY + viewH - 75);

    ctx.textAlign = 'right';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText('SHOCK RIFLE [20]', viewX + viewW - 30, viewY + viewH - 75);

    // Prominent Launch Button
    const btnW = 420;
    const btnH = 46;
    const btnX = viewX + viewW / 2 - btnW / 2;
    const btnY = viewY + viewH - 60;

    ctx.fillStyle = 'rgba(255, 170, 0, 0.2)';
    ctx.strokeStyle = '#ffaa00';
    ctx.lineWidth = 2;
    ctx.fillRect(btnX, btnY, btnW, btnH);
    ctx.strokeRect(btnX, btnY, btnW, btnH);

    ctx.font = 'bold 16px "Orbitron", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText('⚡ LAUNCH TOILLE-FPS (fps.toille.uk)', viewX + viewW / 2, btnY + 28);
    ctx.textAlign = 'left';

    ctx.restore();
  }

  private renderBottomPanel(w: number, h: number) {
    const { ctx } = this;
    const panelY = 515;
    const panelH = h - panelY - 15;
    const panelX = 24;
    const panelW = w - 48;

    // Terminal container box
    ctx.fillStyle = 'rgba(8, 12, 22, 0.95)';
    ctx.fillRect(panelX, panelY, panelW, panelH);
    ctx.strokeStyle = 'rgba(0, 255, 242, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(panelX, panelY, panelW, panelH);

    // Left: Terminal logs
    ctx.font = '12px "Share Tech Mono", monospace';
    let logY = panelY + 24;
    this.terminalLogs.forEach((log) => {
      ctx.fillStyle = log.includes('CARTRIDGE')
        ? '#ff007f'
        : log.includes('TARGET')
        ? '#00fff2'
        : '#64748b';
      ctx.fillText(log, panelX + 16, logY);
      logY += 19;
    });

    // Right: Quick Key Guide
    const guideX = panelX + panelW - 320;
    ctx.fillStyle = '#101726';
    ctx.fillRect(guideX, panelY + 10, 305, panelH - 20);
    ctx.strokeStyle = '#334155';
    ctx.strokeRect(guideX, panelY + 10, 305, panelH - 20);

    ctx.font = 'bold 11px "Share Tech Mono", monospace';
    ctx.fillStyle = '#00fff2';
    ctx.fillText('HOTKEYS / COMMANDS:', guideX + 14, panelY + 28);

    ctx.font = '11px "Share Tech Mono", monospace';
    ctx.fillStyle = '#e2e8f0';
    ctx.fillText('[1] RACER   [2] IDE   [3] FPS', guideX + 14, panelY + 48);
    ctx.fillText('[SPACE / ENTER] LAUNCH SELECTION', guideX + 14, panelY + 68);
    ctx.fillText('[C] SWITCH CAMERA VIEW', guideX + 14, panelY + 88);
    ctx.fillText('[M] MUTE / UNMUTE CYBER AUDIO', guideX + 14, panelY + 108);
  }

  private renderScanlines(w: number, h: number) {
    const { ctx } = this;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
    for (let y = 0; y < h; y += 4) {
      ctx.fillRect(0, y, w, 2);
    }
  }
}
